import { logger } from '../utils/logger';
import type { ActiveContext, Document, DocumentChunk, DocumentContextChunk } from '../types';
import { extractTextFromFile } from '../utils/textExtractor';
import {
  buildDocumentChunks,
  looksLikeSpreadsheetDocument,
  queryNeedsFullDocumentContext,
  rebuildDocumentTextFromChunks,
  selectRelevantDocumentChunks,
} from '../utils/documentChunks';

const STORAGE_KEY = 'rai_documents';
const FULL_DOCUMENT_QUERY_BUDGET = 99_500;
const FULL_DOCUMENT_QUERY_OVERHEAD = 80;

export interface UploadProgress {
  loaded: number;
  total: number;
  percentage: number;
}

export interface FileUploadResponse {
  documentId: string;
  fileName: string;
  fileSize: number;
  success: boolean;
  errorMessage?: string;
}

interface StoredDocument extends Document {
  content?: string;
  chunks?: DocumentChunk[];
}

function buildFullDocumentContexts(
  documents: StoredDocument[],
  query: string,
  activeContext?: ActiveContext
): DocumentContextChunk[] {
  if (!queryNeedsFullDocumentContext(query, activeContext)) {
    return [];
  }

  const tableLikeDocs = documents.filter((doc) =>
    looksLikeSpreadsheetDocument(doc.fileName, doc.contentType) ||
    (doc.content || rebuildDocumentTextFromChunks(doc.chunks || [])).includes('## Table:')
  );
  if (tableLikeDocs.length === 0) {
    return [];
  }

  const availableChars = FULL_DOCUMENT_QUERY_BUDGET - query.length - FULL_DOCUMENT_QUERY_OVERHEAD;
  if (availableChars <= 0) {
    return [];
  }

  const fullContexts: DocumentContextChunk[] = [];
  let totalChars = 0;

  for (const doc of tableLikeDocs) {
    const content = doc.content || rebuildDocumentTextFromChunks(doc.chunks || []);
    if (!content) continue;
    if (totalChars + content.length > availableChars) {
      return [];
    }

    fullContexts.push({
      fileName: doc.fileName,
      content,
      chunkIndex: 0,
      totalChunks: 1,
      relevanceScore: 1_000,
      selectionMode: 'full_document',
    });
    totalChars += content.length;
  }

  return fullContexts;
}

class DocumentService {
  private getStoredDocuments(): StoredDocument[] {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      logger.error('Failed to parse stored documents:', error);
      return [];
    }
  }

  private saveDocuments(documents: StoredDocument[]): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(documents));
    } catch (error) {
      logger.error('Failed to save documents to localStorage:', error);
      if (error instanceof DOMException && error.name === 'QuotaExceededError') {
        logger.warn('localStorage quota exceeded, clearing old documents');
        localStorage.removeItem(STORAGE_KEY);
      }
    }
  }

  async getDocuments(conversationId?: string): Promise<Document[]> {
    const allDocs = this.getStoredDocuments();
    const filtered = conversationId
      ? allDocs.filter((doc) => doc.conversationId === conversationId)
      : allDocs;

    return filtered.map(({ content, chunks, ...doc }) => doc);
  }

  async uploadDocument(
    file: File,
    conversationId?: string,
    onProgress?: (progress: UploadProgress) => void
  ): Promise<Document> {
    try {
      if (onProgress) {
        onProgress({ loaded: 0, total: file.size, percentage: 0 });
      }

      const extractedText = await extractTextFromFile(file);
      const chunks = buildDocumentChunks(extractedText, file.name);
      logger.debug(
        `Document extraction complete: ${extractedText.length} characters from ${file.name} across ${chunks.length} chunks`
      );

      if (onProgress) {
        onProgress({ loaded: file.size, total: file.size, percentage: 100 });
      }

      const documentId = `doc_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
      const newDoc: StoredDocument = {
        id: documentId,
        fileName: file.name,
        originalFileName: file.name,
        contentType: file.type,
        fileSize: file.size,
        uploadDate: new Date(),
        userId: 'user-1',
        conversationId,
        content: extractedText.length <= 20_000 ? extractedText : undefined,
        chunks,
      };

      const allDocs = this.getStoredDocuments();
      allDocs.push(newDoc);
      this.saveDocuments(allDocs);

      logger.debug(
        `Document uploaded to localStorage: ${file.name} (${file.size} bytes, ${extractedText.length} chars extracted, ${chunks.length} chunks)`
      );

      const { content, chunks: _chunks, ...doc } = newDoc;
      return doc;
    } catch (error) {
      logger.error('Failed to upload document:', error);
      throw new Error('Failed to store document');
    }
  }

  async deleteDocument(documentId: string): Promise<void> {
    const allDocs = this.getStoredDocuments();
    const filtered = allDocs.filter((doc) => doc.id !== documentId);
    this.saveDocuments(filtered);
    logger.debug(`Document deleted from localStorage: ${documentId}`);
  }

  getDocumentContent(documentId: string): string | null {
    const allDocs = this.getStoredDocuments();
    const doc = allDocs.find((item) => item.id === documentId);
    if (!doc) return null;
    if (doc.content) return doc.content;
    if (doc.chunks?.length) return doc.chunks.map((chunk) => chunk.content).join('\n\n');
    return null;
  }

  getConversationDocumentContents(conversationId: string): Array<{ fileName: string; content: string }> {
    const allDocs = this.getStoredDocuments();
    return allDocs
      .filter((doc) => doc.conversationId === conversationId)
      .map((doc) => ({
        fileName: doc.fileName,
        content: doc.content || doc.chunks?.map((chunk) => chunk.content).join('\n\n') || '',
      }));
  }

  getRelevantConversationDocumentContents(
    conversationId: string,
    query: string,
    activeContext?: ActiveContext
  ): DocumentContextChunk[] {
    const conversationDocs = this.getStoredDocuments()
      .filter((doc) => doc.conversationId === conversationId);

    const fullDocumentContexts = buildFullDocumentContexts(conversationDocs, query, activeContext);
    if (fullDocumentContexts.length > 0) {
      logger.debug(
        `Using full-document spreadsheet context for "${query}" across ${fullDocumentContexts.length} file(s)`
      );
      return fullDocumentContexts;
    }

    const allDocs = conversationDocs
      .map((doc) => ({
        fileName: doc.fileName,
        contentType: doc.contentType,
        content: doc.content,
        chunks: doc.chunks?.length ? doc.chunks : buildDocumentChunks(doc.content || '', doc.fileName),
      }))
      .filter((doc) => doc.chunks.length > 0);

    return selectRelevantDocumentChunks(allDocs, query, activeContext);
  }
}

export const documentService = new DocumentService();
